import React from "react";
import PropTypes from "prop-types";

import { clx } from "../../../utils/Helpers";
import "./_UIChart.scss";

/** Point on a circle — `angle` in degrees, 0° = 12 o'clock, growing clockwise. */
const polar = (cx, cy, r, angle) => {
  const rad = ((angle - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

/** Stroke arc from `start` to `end` degrees, drawn clockwise. */
const arcPath = (cx, cy, r, start, end) => {
  const [x0, y0] = polar(cx, cy, r, start);
  const [x1, y1] = polar(cx, cy, r, end);
  const largeArc = end - start > 180 ? 1 : 0;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1}`;
};

/**
 * Dependency-free donut / pie chart (inline SVG).
 *
 * `data`: `[{ id, label, value, color }]` — `color` is any CSS colour
 * (token var recommended). Segments run in array order, clockwise from the top,
 * with a small surface gap between them. Pass `children` to fill the centre.
 */
const UIChart = ({
  data,
  size,
  thickness,
  gap,
  trackColor,
  ariaLabel,
  className,
  children,
}) => {
  const segments = data.filter((item) => Number(item.value) > 0);
  const total = segments.reduce((sum, item) => sum + Number(item.value), 0);

  const radius = (size - thickness) / 2;
  const center = size / 2;
  const gapDeg =
    segments.length > 1 && total > 0
      ? (gap / (2 * Math.PI * radius)) * 360
      : 0;

  let cursor = 0;
  const arcs = segments.map((item) => {
    const sweep = (Number(item.value) / total) * 360;
    const start = cursor + gapDeg / 2;
    const end = cursor + sweep - gapDeg / 2;
    cursor += sweep;
    return { item, start, end };
  });

  return (
    <div
      className={clx("ui-chart", className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={ariaLabel}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={thickness}
          style={{ stroke: trackColor }}
        />
        {arcs.map(({ item, start, end }) =>
          end - start >= 359.999 ? (
            <circle
              key={item.id}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              strokeWidth={thickness}
              style={{ stroke: item.color }}
            >
              <title>{`${item.label}: ${item.value}`}</title>
            </circle>
          ) : (
            <path
              key={item.id}
              d={arcPath(center, center, radius, start, end)}
              fill="none"
              strokeLinecap="butt"
              strokeWidth={thickness}
              style={{ stroke: item.color }}
            >
              <title>{`${item.label}: ${item.value}`}</title>
            </path>
          ),
        )}
      </svg>
      {children != null && <div className="ui-chart__center">{children}</div>}
    </div>
  );
};

UIChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  size: PropTypes.number,
  thickness: PropTypes.number,
  gap: PropTypes.number,
  trackColor: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

UIChart.defaultProps = {
  size: 180,
  thickness: 22,
  gap: 4,
  trackColor: "var(--color-border-default)",
  ariaLabel: "Chart",
  className: "",
  children: null,
};

export default UIChart;
